import type { GraphData } from "../../services/dataLoader";
import { unifiedDataStore } from "../../services/UnifiedDataStore";
import type { GameReport } from "../../utils/gameReportUtils";
import { allWordCollections } from "../wordCollections/definitions";
import type { WordCollection } from "../wordCollections/collection.types";
import { filterWordCollections } from "../wordCollections/logic";
import {
  collectWordsFromPath,
  isCollectionComplete,
  isSeasonalCollection,
} from "../wordCollections/progress";
import type { Achievement } from "./achievement.types";
import { seasonalExplorerAchievement } from "./definitions/seasonalExplorer.achievement";
import { wordCollectorAchievement } from "./definitions/wordCollector.achievement";

export interface CollectionAwardResult {
  /** Collections that became complete during this call. */
  newlyCompleted: string[];
  /** Achievements whose counter went up during this call. */
  awarded: Achievement[];
  /** Achievement ids unlocked for the first time during this call. */
  newlyUnlocked: string[];
}

/**
 * Raise a progressive counter to `target`. Counters are derived from the set
 * of completed collections, so re-running never double-counts and a stored
 * completion whose award was never counted is backfilled.
 */
const raiseCounterTo = async (
  achievement: Achievement,
  target: number,
  result: CollectionAwardResult,
): Promise<void> => {
  let count = await unifiedDataStore.getProgressiveAchievementCount(
    achievement.id,
  );
  if (count >= target) return;
  const wasLocked = count === 0;
  while (count < target) {
    count = await unifiedDataStore.incrementProgressiveAchievement(
      achievement.id,
    );
  }
  result.awarded.push(achievement);
  if (wasLocked) result.newlyUnlocked.push(achievement.id);
};

/**
 * Mark every collection whose words are all collected as complete, then set
 * Word Collector to the number of completed collections and Seasonal
 * Explorer to the number of completed seasonal ones.
 */
export const syncCollectionAchievements = async (
  collections: readonly WordCollection[],
): Promise<CollectionAwardResult> => {
  const result: CollectionAwardResult = {
    newlyCompleted: [],
    awarded: [],
    newlyUnlocked: [],
  };
  const progress = await unifiedDataStore.getCollectionProgress();
  const completed = new Set(
    await unifiedDataStore.getCompletedWordCollections(),
  );

  for (const collection of collections) {
    if (completed.has(collection.id)) continue;
    const collected = progress[collection.id]?.collectedWords ?? [];
    if (!isCollectionComplete(collection, collected)) continue;
    await unifiedDataStore.markWordCollectionAsCompleted(collection.id);
    completed.add(collection.id);
    result.newlyCompleted.push(collection.id);
  }

  const done = collections.filter((c) => completed.has(c.id));
  await raiseCounterTo(wordCollectorAchievement, done.length, result);
  await raiseCounterTo(
    seasonalExplorerAchievement,
    done.filter(isSeasonalCollection).length,
    result,
  );
  return result;
};

/** Record a finished game's collected words, then award any completions. */
export const awardCollectionProgress = async (
  playerPath: readonly string[],
  collections: readonly WordCollection[],
  date: Date = new Date(),
): Promise<CollectionAwardResult> => {
  const collected = collectWordsFromPath(collections, playerPath, date);
  for (const [collectionId, words] of Object.entries(collected)) {
    for (const word of words) {
      await unifiedDataStore.recordWordForCollection(collectionId, word);
    }
  }
  return syncCollectionAchievements(collections);
};

/**
 * After a finished (non-tutorial) game: collect its words and add any
 * collection awards to the report, so the game that completes a collection
 * shows the award. Failures are logged and never block the end of a game.
 */
export const applyCollectionAwards = async (
  report: GameReport,
  playerPath: readonly string[],
  graphData: GraphData,
): Promise<void> => {
  if (report.isTutorial) return;
  try {
    const collections = filterWordCollections(allWordCollections, graphData);
    const result = await awardCollectionProgress(playerPath, collections);
    report.earnedAchievements = [
      ...(report.earnedAchievements ?? []),
      ...result.awarded,
    ];
    report.newAchievementIds = [
      ...(report.newAchievementIds ?? []),
      ...result.newlyUnlocked,
    ];
  } catch (error) {
    console.error("Failed to update word collection progress:", error);
  }
};
